/* ORIO — light, warm, premium PWA primitives
   Inspired by Airbnb, Apple Fitness, Google Maps mobile.
   API is preserved — pages don't need to change usage.
   ----------------------------------------------------------
   - Backgrounds: white / warm ivory
   - Text: #1A1A1A primary, #6B6B6B secondary
   - Functional colors used contextually:
       red=emergency, orange=primary, yellow=community, green=safety
*/
import React from "react";
import ReactDOM from "react-dom";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

/* ---------------- Carousel: snap-x horizontal scroll for thumb swipe ---------------- */
export function Carousel({ children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`-mx-4 sm:-mx-0 px-4 sm:px-0 flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-1 ${className}`}
      style={{ scrollPadding: "0 1rem" }}
    >
      {React.Children.map(children, (child) => (
        <div className="snap-start shrink-0">{child}</div>
      ))}
    </div>
  );
}

/* ---------------- Stagger: animated wrapper with cascade entrance ---------------- */
export function Stagger({ children, delay = 0, gap = 0.06, className = "", as = "div" }) {
  const Component = motion[as] || motion.div;
  return (
    <Component
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: gap, delayChildren: delay } },
      }}
    >
      {React.Children.map(children, (child, i) => (
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 10 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] } },
          }}
          key={i}
        >
          {child}
        </motion.div>
      ))}
    </Component>
  );
}

/* ---------------- CountUp: animated number that counts from 0 on mount/inview ---------------- */
export function CountUp({ value = 0, duration = 1.1, className = "" }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20px" });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v).toLocaleString("it-IT"));
  React.useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, {
      duration,
      ease: [0.22, 0.61, 0.36, 1],
    });
    return controls.stop;
  }, [inView, value, duration, mv]);
  return <motion.span ref={ref} className={className}>{rounded}</motion.span>;
}

/* ---------------- Section: vertical grouping with eyebrow ---------------- */
export function Section({ eyebrow, title, action, children, className = "" }) {
  return (
    <section className={`mt-7 sm:mt-9 ${className}`}>
      {(eyebrow || title || action) && (
        <div className="px-1 mb-3 flex items-end justify-between gap-3">
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] font-medium">
                {eyebrow}
              </div>
            )}
            {title && (
              <h2 className="font-display text-[22px] sm:text-2xl tracking-tight leading-tight mt-1 text-[#1A1A1A]">
                {title}
              </h2>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ---------------- List: iOS-style grouped list (white surface, soft shadow) ---------------- */
export function List({ children, className = "" }) {
  return (
    <div
      className={`bg-white rounded-3xl overflow-hidden border border-[#F0EBE3] divide-y divide-[#F4EFE6] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)] ${className}`}
    >
      {children}
    </div>
  );
}

/* ---------------- Row: clickable iOS-style row ---------------- */
export function Row({
  icon: Icon, iconColor, label, sub, value, to, onClick, chevron = true,
  danger = false, testid,
}) {
  const Wrap = to ? Link : onClick ? "button" : "div";
  const MotionWrap = motion(Wrap);
  const props = to ? { to } : onClick ? { onClick, type: "button" } : {};
  const interactive = !!(to || onClick);
  const accent = iconColor || "#FF7A1A";
  return (
    <MotionWrap
      data-testid={testid}
      {...props}
      whileTap={interactive ? { scale: 0.985 } : undefined}
      transition={{ type: "spring", stiffness: 480, damping: 30 }}
      className={`w-full text-left flex items-center gap-3 px-4 sm:px-5 py-3.5 ${interactive ? "hover:bg-[#FAF7F0] active:bg-[#F5F0E6] transition-colors" : ""}`}
    >
      {Icon && (
        <div
          className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: `${accent}1F`, color: accent }}
        >
          <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-[15px] leading-tight font-medium ${danger ? "text-[#E94545]" : "text-[#1A1A1A]"}`}>
          {label}
        </div>
        {sub && <div className="text-[12.5px] text-[#9A9A9A] mt-0.5 truncate">{sub}</div>}
      </div>
      {value && <div className="text-[13px] text-[#6B6B6B] shrink-0 truncate max-w-[40%]">{value}</div>}
      {interactive && chevron && <ChevronRight className="h-4 w-4 text-[#C7C0B3] shrink-0" />}
    </MotionWrap>
  );
}

/* ---------------- HeroNumber: legacy stat (kept for backward compat) ---------------- */
export function HeroNumber({ value, label, color = "#1A1A1A", className = "" }) {
  const isNumber = typeof value === "number" || (typeof value === "string" && /^\d+$/.test(value));
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <div
        className="font-display tracking-tight leading-none tabular-nums"
        style={{ fontSize: "clamp(48px, 10vw, 88px)", color }}
      >
        {isNumber ? <CountUp value={Number(value)} /> : value}
      </div>
      <div className="text-[12px] sm:text-sm text-[#6B6B6B] mt-1">{label}</div>
    </motion.div>
  );
}

/* ---------------- StatTile: small stat card with colored icon (Airbnb-style)
   Used by Home for "Missioni attive / Cani aiutati / Rifugi connessi" ---------------- */
export function StatTile({ icon: Icon, iconColor = "#FF7A1A", value, label, testid }) {
  return (
    <motion.div
      data-testid={testid}
      whileTap={{ scale: 0.98 }}
      className="bg-white rounded-3xl p-4 border border-[#F0EBE3] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)] flex-1 min-w-0"
    >
      <div className="text-[10px] uppercase tracking-wider text-[#9A9A9A] mb-1.5 leading-tight">{label}</div>
      <div className="flex items-end justify-between gap-2">
        <div className="font-display text-3xl tracking-tight leading-none text-[#1A1A1A] tabular-nums">
          {typeof value === "number" || /^\d+$/.test(String(value)) ? (
            <CountUp value={Number(value)} duration={0.9} />
          ) : (
            value
          )}
        </div>
        {Icon && (
          <div
            className="h-8 w-8 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${iconColor}1F`, color: iconColor }}
          >
            <Icon className="h-4 w-4" strokeWidth={2.2} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ---------------- PhotoCard: Airbnb-style hero card with photo (warm overlay) ---------------- */
export function PhotoCard({ to, photo, eyebrow, eyebrowColor = "#FF7A1A", title, subtitle, meta, action, testid }) {
  const Wrap = to ? Link : "div";
  const props = to ? { to } : {};
  return (
    <Wrap
      data-testid={testid}
      {...props}
      className="group block rounded-3xl overflow-hidden relative border border-[#F0EBE3] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] active:scale-[0.99] transition-transform bg-white"
    >
      <div className="relative aspect-[5/3] bg-[#F4EFE6]">
        {photo ? (
          <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0" style={{
            background: `radial-gradient(60% 60% at 50% 60%, ${eyebrowColor}22 0%, rgba(0,0,0,0) 70%)`,
          }} />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.78) 100%)" }} />
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 text-white">
          {eyebrow && (
            <div className="text-[10px] uppercase tracking-[0.22em] mb-1 font-medium" style={{ color: eyebrowColor }}>
              {eyebrow}
            </div>
          )}
          <div className="font-display text-xl sm:text-2xl leading-tight max-w-[80%]">{title}</div>
          {subtitle && <div className="text-[13px] text-white/85 mt-1 line-clamp-2 max-w-[90%]">{subtitle}</div>}
          {(meta || action) && (
            <div className="mt-3 flex items-center gap-2">
              {meta && <div className="text-[11px] text-white/70">{meta}</div>}
              <div className="flex-1" />
              {action}
            </div>
          )}
        </div>
      </div>
    </Wrap>
  );
}

/* ---------------- ThumbCTA: sticky bottom CTA (thumb zone, mobile only) ---------------- */
export function ThumbCTA({ children }) {
  return (
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-40 pt-3 px-4"
      style={{
        background: "linear-gradient(180deg, rgba(251,247,241,0) 0%, rgba(251,247,241,0.96) 60%)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)",
      }}
    >
      {children}
    </div>
  );
}

/* ---------------- Sheet: iOS-style bottom sheet — light, warm surface ---------------- */
export function Sheet({ open, onClose, title, eyebrow, children, footer, testid }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.classList.add("orio-sheet-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      document.body.classList.remove("orio-sheet-open");
    };
  }, [open, onClose]);
  if (!open || typeof document === "undefined") return null;
  const node = (
    <div
      data-testid={testid}
      className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/45 backdrop-blur-md" />
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 28, stiffness: 280 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md mx-auto rounded-t-[28px] sm:rounded-3xl border border-[#F0EBE3] overflow-hidden bg-white shadow-[0_-20px_40px_-12px_rgba(0,0,0,0.18)]"
        style={{ maxHeight: "92vh" }}
      >
        {/* iOS grabber */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="h-1 w-9 rounded-full bg-[#E8E2D6]" />
        </div>
        {(eyebrow || title) && (
          <div className="px-5 pt-3 sm:pt-5 pb-3">
            {eyebrow && (
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] font-medium mb-1">{eyebrow}</div>
            )}
            {title && <div className="font-display text-xl sm:text-2xl tracking-tight text-[#1A1A1A]">{title}</div>}
          </div>
        )}
        <div className="px-5 pb-4 overflow-y-auto" style={{ maxHeight: "calc(92vh - 200px)" }}>
          {children}
        </div>
        {footer && (
          <div className="px-5 pt-3 border-t border-[#F0EBE3] bg-[#FBF7F1]"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
            {footer}
          </div>
        )}
      </motion.div>
    </div>
  );
  return ReactDOM.createPortal(node, document.body);
}

/* ---------------- EmptyState: illustrated empty state with CTA ---------------- */
export function EmptyState({ icon: Icon, title, body, cta, accent = "#FF7A1A", testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-3xl p-8 border border-[#F0EBE3] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)] text-center"
    >
      {Icon && (
        <div
          className="h-16 w-16 rounded-3xl flex items-center justify-center mx-auto mb-4"
          style={{ background: `${accent}14`, color: accent }}
        >
          <Icon className="h-7 w-7" strokeWidth={1.8} />
        </div>
      )}
      <h3 className="font-display text-xl text-[#1A1A1A] tracking-tight">{title}</h3>
      {body && <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">{body}</p>}
      {cta && <div className="mt-5 flex justify-center">{cta}</div>}
    </div>
  );
}

/* ---------------- Chip: pill filter (selectable) ---------------- */
export function Chip({ active, onClick, icon: Icon, children, testid, color = "#FF7A1A" }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      type="button"
      className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all border ${
        active
          ? "text-white shadow-[0_4px_12px_-4px_rgba(0,0,0,0.20)]"
          : "bg-white text-[#1A1A1A] border-[#F0EBE3] hover:border-[#D8CFB6]"
      }`}
      style={active ? { background: color, borderColor: color } : undefined}
    >
      {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />}
      {children}
    </button>
  );
}
